import React, { useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import printButtonStyle from './print-document-button.module.less';
import sharedStyles from '../../components/grid-header/shared-classes.module.less';
import { emptyArray } from 'ui-components/default-props/empty';
import { MessageDescriptorPropType } from '../../prop-types/intl';
import PrintDocumentMenuItem from '../print-document-menu-item';
import messages from './messages';

const RegisteredHotkey = ({ Component, data, handler }) => {
  const hotkeyHandler = useCallback(() => {
    handler(data);
  }, [data, handler]);

  return <Component handler={hotkeyHandler} />;
};

RegisteredHotkey.propTypes = {
  Component: PropTypes.elementType.isRequired,
  data: PropTypes.any.isRequired,
  handler: PropTypes.func.isRequired,
};

const RegisteredHotkeys = ({ data, dropdownItems }) => {
  return dropdownItems
    .filter((item) => item.Hotkey)
    .map((item, index) => <RegisteredHotkey key={index} Component={item.Hotkey} data={data} handler={item.onClick} />);
};

const PrintDocumentButton = ({
  children = null,
  dropdownItems = emptyArray,
  data = emptyArray,
  theme = '',
  disabled = false,
  dropdownTogglerClassname,
  unstyled = false, // disables built in styling specific to the print button
}) => {
  const isRevision2Tertiary = theme === 'tertiary';

  const dropdownClassnames = classnames({ [sharedStyles['action-bar-button']]: !unstyled });
  const dropdownMenuClassnames = classnames({ [printButtonStyle['dropdown-menu']]: !unstyled && !isRevision2Tertiary });
  const dropdownTogglerClassnames = classnames(
    { [printButtonStyle['action-button']]: !unstyled && !isRevision2Tertiary },
    dropdownTogglerClassname,
  );

  const dropdownMenu = (
    <DropdownMenu
      className={dropdownClassnames}
      disabled={disabled}
      dropdownMenuClassName={dropdownMenuClassnames}
      menuPosition='bottom-right'
    >
      <DropdownMenuToggler className={dropdownTogglerClassnames} theme={theme}>
        <FormattedMessage {...messages.print} />
      </DropdownMenuToggler>
      {children ? children : null}
      {!children &&
        dropdownItems.map((printAction, index) => {
          return <PrintDocumentMenuItem {...printAction} key={index} data={data} />;
        })}
    </DropdownMenu>
  );

  if (disabled || children) {
    return dropdownMenu;
  }

  return (
    <React.Fragment>
      <RegisteredHotkeys dropdownItems={dropdownItems} />
      {dropdownMenu}
    </React.Fragment>
  );
};

PrintDocumentButton.propTypes = {
  children: PropTypes.node,
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: MessageDescriptorPropType,
      disabled: PropTypes.bool,
      onClick: PropTypes.func,
      defaultPrintSetting: PropTypes.shape({
        messageDescriptor: MessageDescriptorPropType,
        displayText: PropTypes.string,
        onClick: PropTypes.func.isRequired,
      }),
    }),
  ),
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  theme: PropTypes.string,
  disabled: PropTypes.bool,
};

export default PrintDocumentButton;
